import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { Loader2, Upload, FileText, Image as ImageIcon, Trash2, Check, Save } from "lucide-react";

const SEGMENT_ORDER = [
  "Clinical Chemistry", "Hematology", "Critical Care", "Urinalysis",
  "Hemostasis", "ESR", "Diabetes Management", "Immunology",
];

export default function AdminInstruments() {
  const [instruments, setInstruments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [uploading, setUploading] = useState({}); // key = `${code}_photo` / `_brochure` / `_desc`
  const [statusMsg, setStatusMsg] = useState(null); // { code, type, text }
  const [descDraft, setDescDraft] = useState({}); // code → current description value
  const [tagDraft, setTagDraft] = useState({});   // code → current tagline value
  const [videoDraft, setVideoDraft] = useState({}); // code → current video URL value
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter ? `?segment=${encodeURIComponent(filter)}` : "";
      const { data } = await API.get(`/admin/instruments${params}`);
      setInstruments(data || []);
      // Seed editable drafts so textareas/inputs start in-sync with server state
      const seedDesc = {}; const seedTag = {}; const seedVid = {};
      (data || []).forEach(i => {
        seedDesc[i.instrument_code] = i.description || "";
        seedTag[i.instrument_code] = i.tagline || "";
        seedVid[i.instrument_code] = i.video_url || "";
      });
      setDescDraft(seedDesc);
      setTagDraft(seedTag);
      setVideoDraft(seedVid);
    } catch {} finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const flash = (code, type, text) => {
    setStatusMsg({ code, type, text });
    setTimeout(() => setStatusMsg(null), 2500);
  };

  const saveDescription = async (code) => {
    const k = `${code}_desc`;
    const value = (descDraft[code] || "").trim();
    setUploading(p => ({ ...p, [k]: true }));
    try {
      await API.put(`/admin/instruments/${encodeURIComponent(code)}`, { description: value });
      flash(code, "success", "Description saved");
      // Reflect new value locally without a full reload
      setInstruments(list => list.map(i => i.instrument_code === code ? { ...i, description: value } : i));
    } catch (e) {
      flash(code, "error", e.response?.data?.detail || "Could not save description");
    } finally { setUploading(p => ({ ...p, [k]: false })); }
  };

  const saveTagline = async (code) => {
    const k = `${code}_tag`;
    const value = (tagDraft[code] || "").trim();
    setUploading(p => ({ ...p, [k]: true }));
    try {
      await API.put(`/admin/instruments/${encodeURIComponent(code)}`, { tagline: value });
      flash(code, "success", "Tagline saved");
      setInstruments(list => list.map(i => i.instrument_code === code ? { ...i, tagline: value } : i));
    } catch (e) {
      flash(code, "error", e.response?.data?.detail || "Could not save tagline");
    } finally { setUploading(p => ({ ...p, [k]: false })); }
  };

  const saveVideo = async (code) => {
    const k = `${code}_video`;
    const value = (videoDraft[code] || "").trim();
    setUploading(p => ({ ...p, [k]: true }));
    try {
      await API.put(`/admin/instruments/${encodeURIComponent(code)}`, { video_url: value });
      flash(code, "success", value ? "Video link saved" : "Video link cleared");
      setInstruments(list => list.map(i => i.instrument_code === code ? { ...i, video_url: value } : i));
    } catch (e) {
      flash(code, "error", e.response?.data?.detail || "Could not save video link");
    } finally { setUploading(p => ({ ...p, [k]: false })); }
  };

  const uploadPhoto = async (code, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { flash(code, "error", "Only images allowed"); return; }
    const k = `${code}_photo`;
    setUploading(p => ({ ...p, [k]: true }));
    const fd = new FormData(); fd.append("file", file);
    try {
      await API.post(`/admin/instruments/${encodeURIComponent(code)}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      flash(code, "success", "Photo updated");
      load();
    } catch (e) {
      flash(code, "error", e.response?.data?.detail || "Photo upload failed");
    } finally { setUploading(p => ({ ...p, [k]: false })); }
  };

  const uploadBrochure = async (code, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) { flash(code, "error", "Brochure must be a PDF"); return; }
    const k = `${code}_brochure`;
    setUploading(p => ({ ...p, [k]: true }));
    const fd = new FormData(); fd.append("file", file);
    try {
      await API.post(`/admin/instruments/${encodeURIComponent(code)}/brochure`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      flash(code, "success", "Brochure uploaded");
      load();
    } catch (e) {
      flash(code, "error", e.response?.data?.detail || "Brochure upload failed");
    } finally { setUploading(p => ({ ...p, [k]: false })); }
  };

  const deleteBrochure = async (code) => {
    if (!window.confirm("Remove brochure for " + code + "?")) return;
    try {
      await API.delete(`/admin/instruments/${encodeURIComponent(code)}/brochure`);
      flash(code, "success", "Brochure removed");
      load();
    } catch (e) {
      flash(code, "error", "Failed to remove brochure");
    }
  };

  // Group instruments by segment for nice display
  const grouped = SEGMENT_ORDER.reduce((acc, seg) => {
    const list = instruments.filter(i => i.segment === seg);
    if (list.length) acc.push({ segment: seg, list });
    return acc;
  }, []);
  // Catch any segment not in our preferred order
  for (const i of instruments) {
    if (!SEGMENT_ORDER.includes(i.segment) && i.segment) {
      const existing = grouped.find(g => g.segment === i.segment);
      if (existing) existing.list.push(i);
      else grouped.push({ segment: i.segment, list: [i] });
    }
  }

  return (
    <div className="space-y-6" data-testid="admin-instruments">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Instruments</h2>
          <p className="text-sm text-gray-500 mt-0.5">Upload photos and PDF brochures per instrument code</p>
        </div>
        <select value={filter} onChange={e => setFilter(e.target.value)}
          className="h-10 rounded-xl border border-gray-200 px-3 text-sm bg-white focus:border-[#008A99]"
          data-testid="instruments-segment-filter">
          <option value="">All segments</option>
          {SEGMENT_ORDER.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#008A99]" /></div>
      ) : (
        <div className="space-y-6">
          {grouped.map(({ segment, list }) => (
            <div key={segment}>
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">{segment} ({list.length})</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {list.map(inst => {
                  const code = inst.instrument_code;
                  const imgKey = `${code}_photo`;
                  const brKey = `${code}_brochure`;
                  const showMsg = statusMsg && statusMsg.code === code;
                  return (
                    <div key={code} className="bg-white border border-gray-200 rounded-2xl p-3 shadow-sm" data-testid={`instrument-card-${code}`}>
                      {/* Photo preview + upload */}
                      <div className="flex gap-3">
                        <div className="w-20 h-20 rounded-xl bg-gray-50 flex items-center justify-center overflow-hidden flex-shrink-0 border border-gray-100">
                          {inst.image_url ? (
                            <img src={`${BACKEND_URL}${inst.image_url}`} alt={code} className="w-full h-full object-contain" onError={e => { e.target.style.display = 'none'; }} />
                          ) : (
                            <ImageIcon className="w-7 h-7 text-gray-300" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-sm text-gray-900 truncate">{code}</p>
                          <p className="text-[10px] text-gray-400 mt-0.5">{inst.system_type === "open" ? "Open System" : inst.system_type === "close" ? "Closed System" : "Open & Closed"}</p>
                          <label className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#008A99] hover:bg-[#E0F4F5] px-2 py-1 rounded-md cursor-pointer">
                            {uploading[imgKey] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                            {inst.image_url ? "Replace photo" : "Upload photo"}
                            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                                   onChange={e => { const f = e.target.files[0]; if (f) uploadPhoto(code, f); e.target.value = ""; }}
                                   data-testid={`upload-photo-${code}`} />
                          </label>
                        </div>
                      </div>
                      {/* Brochure row */}
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <div className="flex items-center gap-2">
                          <FileText className={`w-4 h-4 flex-shrink-0 ${inst.brochure_url ? "text-[#008A99]" : "text-gray-300"}`} />
                          <div className="flex-1 min-w-0">
                            {inst.brochure_url ? (
                              <a href={`${BACKEND_URL}${inst.brochure_url}`} target="_blank" rel="noopener noreferrer" className="text-xs text-[#008A99] font-semibold truncate block hover:underline" data-testid={`brochure-link-${code}`}>
                                {inst.brochure_filename || "View brochure"}
                              </a>
                            ) : (
                              <span className="text-xs text-gray-400">No brochure yet</span>
                            )}
                          </div>
                          <label className="text-[10px] font-bold text-[#008A99] hover:bg-[#E0F4F5] px-2 py-1 rounded-md cursor-pointer flex items-center gap-1">
                            {uploading[brKey] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                            {inst.brochure_url ? "Replace" : "Upload"}
                            <input type="file" accept="application/pdf,.pdf" className="hidden"
                                   onChange={e => { const f = e.target.files[0]; if (f) uploadBrochure(code, f); e.target.value = ""; }}
                                   data-testid={`upload-brochure-${code}`} />
                          </label>
                          {inst.brochure_url && (
                            <button onClick={() => deleteBrochure(code)} className="text-[10px] text-red-500 hover:bg-red-50 p-1 rounded" data-testid={`delete-brochure-${code}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                      {/* Video URL editor — shown on the customer Videos screen */}
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Video Link</label>
                        <div className="mt-1 flex gap-2">
                          <input
                            type="url"
                            value={videoDraft[code] ?? (inst.video_url || "")}
                            onChange={e => setVideoDraft(d => ({ ...d, [code]: e.target.value }))}
                            placeholder="https://www.youtube.com/watch?v=… or any direct .mp4 URL"
                            className="flex-1 px-2.5 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:border-[#008A99] focus:ring-1 focus:ring-[#008A99]/30"
                            data-testid={`instrument-video-${code}`}
                          />
                          <button
                            onClick={() => saveVideo(code)}
                            disabled={uploading[`${code}_video`] || (videoDraft[code] ?? "") === (inst.video_url ?? "")}
                            className="text-[11px] font-bold text-white bg-[#008A99] hover:bg-[#006670] disabled:bg-gray-200 disabled:text-gray-400 px-3 py-2 rounded-md flex items-center gap-1.5 transition-colors flex-shrink-0"
                            data-testid={`save-video-${code}`}
                          >
                            {uploading[`${code}_video`] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                            Save
                          </button>
                        </div>
                      </div>
                      {/* Tagline editor — single short line shown under product name */}
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Tagline</label>
                        <div className="mt-1 flex gap-2">
                          <input
                            type="text"
                            value={tagDraft[code] ?? (inst.tagline || "")}
                            onChange={e => setTagDraft(d => ({ ...d, [code]: e.target.value }))}
                            placeholder="e.g. Compact semi-automated analyzer for routine assays"
                            maxLength={120}
                            className="flex-1 px-2.5 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:border-[#008A99] focus:ring-1 focus:ring-[#008A99]/30"
                            data-testid={`instrument-tagline-${code}`}
                          />
                          <button
                            onClick={() => saveTagline(code)}
                            disabled={uploading[`${code}_tag`] || (tagDraft[code] ?? "") === (inst.tagline ?? "")}
                            className="text-[11px] font-bold text-white bg-[#008A99] hover:bg-[#006670] disabled:bg-gray-200 disabled:text-gray-400 px-3 py-2 rounded-md flex items-center gap-1.5 transition-colors flex-shrink-0"
                            data-testid={`save-tag-${code}`}
                          >
                            {uploading[`${code}_tag`] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                            Save
                          </button>
                        </div>
                      </div>
                      {/* Description editor — shown on the customer Instrument detail screen */}
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Description</label>
                        <textarea
                          rows={3}
                          value={descDraft[code] ?? (inst.description || "")}
                          onChange={e => setDescDraft(d => ({ ...d, [code]: e.target.value }))}
                          placeholder="Short product blurb shown to customers on the instrument detail screen…"
                          className="mt-1 w-full px-2.5 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:border-[#008A99] focus:ring-1 focus:ring-[#008A99]/30 resize-none"
                          data-testid={`instrument-desc-${code}`}
                        />
                        <div className="flex justify-end mt-1.5">
                          <button
                            onClick={() => saveDescription(code)}
                            disabled={uploading[`${code}_desc`] || (descDraft[code] ?? "") === (inst.description ?? "")}
                            className="text-[11px] font-bold text-white bg-[#008A99] hover:bg-[#006670] disabled:bg-gray-200 disabled:text-gray-400 px-3 py-1.5 rounded-md flex items-center gap-1.5 transition-colors"
                            data-testid={`save-desc-${code}`}
                          >
                            {uploading[`${code}_desc`] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                            Save description
                          </button>
                        </div>
                      </div>
                      {showMsg && (
                        <div className={`mt-2 text-[11px] px-2 py-1 rounded flex items-center gap-1 ${statusMsg.type === "success" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`} data-testid={`instrument-msg-${code}`}>
                          {statusMsg.type === "success" ? <Check className="w-3 h-3" /> : null}
                          {statusMsg.text}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
